/**
 * WorkforceMySkills — "My Skills & Services" (Marcus Thompson, Personal Banker).
 *
 * The services Marcus can deliver. His role's eligible services from the branch
 * catalogue Riley (Operations admin) maintains; ticking one makes him capable of
 * it, unticking removes it and tells his manager. Operator's model, 2026-08-01:
 * instant (no approval), on/off (no levels), no certification evidence, no
 * demand nudging — capability makes him bookable straight away.
 *
 * ── Data ────────────────────────────────────────────────────────────────
 *   getAdvisorRoster() — the list of services the branch runs (its `services`
 *                    legend) AND, on my own row, the ones I already offer.
 *   ServicesStore  — the branch catalogue Riley maintains, used ONLY to enrich
 *                    a row with duration + delivery methods when the signed-in
 *                    role may read it. A Personal Banker has no services.read,
 *                    so it is empty for Marcus and the rows simply carry less.
 *                    Never the source of the list: an empty catalogue would
 *                    otherwise read as "you can offer nothing", which is false.
 *
 * ── Honest gaps (REQ-124 filed; nothing here is faked) ──────────────────
 *   · There is NO self-scoped op. `getMyServices` / `setMyServices` don't
 *     exist, so "mine" is read out of the manager's roster op by matching the
 *     signed-in name, and ticks CANNOT be saved — Save states why and stays
 *     disabled. Building it against ServicesStore would mean a staff surface
 *     writing Riley's service configuration; that is the wrong model, not a
 *     shortcut, so it isn't done.
 *   · Branch scoping is what the roster reports for my branch; role
 *     eligibility is not expressible in any readable shape yet. REQ-124 asks
 *     for eligible + offeredAtMyBranch per service.
 *   · The manager notification on removal has no op either (REQ-124), so the
 *     row says who WILL be told rather than claiming anyone was.
 */
import { useEffect, useMemo, useState } from "react";
import { Btn, T, alpha } from "@v0y4ge/jrni-ui";
import { Screen } from "../../shell/chrome";
import { DataSourceStrip } from "../../shell/opSource";
import { getUser } from "../../shell/access";
import { useWfResource } from "./useWorkforce";
import { getAdvisorRoster, type AdvisorRosterResponse, type AdvisorRow } from "./wf-api";
import { ServicesStore, SVC_METHOD, type Service } from "../services/services-data";
import { useT } from "../../lib/i18n";
import { getMyServiceScope, type MyServicesView } from "./wf-api";

const sStr = (v: unknown) => (typeof v === "string" ? v : "");
const sName = (s: Service) => sStr(s.name);
const sMethods = (s: Service): string[] => (Array.isArray(s.methods) ? s.methods.map(sStr).filter(Boolean) : []);
const sDuration = (s: Service): number => (typeof s.duration === "number" ? s.duration : 0);
const eq = (a: string, b: string) => a.trim().toLowerCase() === b.trim().toLowerCase();
/** The roster records a skill as the service's ID (`home-loans`), not its label.
 *  Match on id first, then label, then a slugified label — the three spellings
 *  the two vocabularies actually use. */
const slug = (s: string) => s.trim().toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");
const skillMatches = (skill: string, svc: { id: string; label: string }) =>
  eq(skill, svc.id) || eq(skill, svc.label) || slug(skill) === slug(svc.label) || slug(skill) === slug(svc.id);

function Switch({ on, onChange, label }: { on: boolean; onChange: (v: boolean) => void; label: string }) {
  return (
    <button role="switch" aria-checked={on} aria-label={label} onClick={() => onChange(!on)} data-testid="myskills-toggle"
      style={{ width: 42, height: 24, flexShrink: 0, borderRadius: 999, border: `1px solid ${on ? T.p1 : T.n5}`, background: on ? T.p1 : T.white, cursor: "pointer", padding: 0, position: "relative", transition: "background 120ms" }}>
      <span style={{ position: "absolute", top: 2, left: on ? 20 : 2, width: 18, height: 18, borderRadius: "50%", background: on ? T.white : T.n4, transition: "left 120ms" }} />
    </button>
  );
}

function MethodChip({ id }: { id: string }) {
  const m = SVC_METHOD(id);
  return (
    <span style={{ fontSize: 10, fontWeight: 700, color: m.hue, background: alpha(m.hue, 10), border: `1px solid ${alpha(m.hue, 28)}`, borderRadius: 999, padding: "1px 8px", whiteSpace: "nowrap" }}>{m.label}</span>
  );
}

export default function WorkforceMySkills() {
  const t = useT();
  const me = getUser();
  const roster = useWfResource<AdvisorRosterResponse>("myskills:roster", () => getAdvisorRoster());
  /* REQ-128: my line of business + the catalogue rows for it. Read-only by construction —
     the op is self-scoped and has no write; only Riley can change the assignment. */
  const scope = useWfResource<MyServicesView>("myskills:scope", () => getMyServiceScope());
  const [tick, setTick] = useState(0); // ui-local — ServicesStore revision
  useEffect(() => ServicesStore.subscribe(() => setTick((n) => n + 1)), []);
  const [picked, setPicked] = useState<Record<string, boolean>>({}); // ui-local — unsaved intent

  const myRow: AdvisorRow | null = useMemo(() => {
    const items = roster.data?.items || [];
    const name = me?.name || "";
    return items.find((r) => eq(r.name, name)) || null;
  }, [roster.data, me]);

  const myRole = myRow?.role || "";
  const myBranch = myRow?.branch || "";
  const mySkills = useMemo(() => (myRow?.skills || []).map((s) => s.toLowerCase()), [myRow]);

  const { items, total, offered } = useMemo(() => {
    void tick; // re-read the store when it emits
    const catalogue = ServicesStore.services();
    const byName = new Map(catalogue.map((s) => [sName(s).toLowerCase(), s] as const));
    /* SOURCE OF THE LIST (2026-08-03): my line of business's catalogue, served by
       getMyServiceScope — so the rows carry intent, duration and channels even though a
       Personal Banker holds no services.read. The advisor roster is the FALLBACK: if the
       catalogue has not resolved, fall back to the branch service list rather than render
       "you can offer nothing", which would be false. */
    const fromCatalogue = (scope.data?.services ?? []).map((c) => ({
      id: c.id, label: c.name, dur: c.durationMin ?? 0,
      methods: [c.channels.inPerson ? "in-person" : "", c.channels.phone ? "phone" : "", c.channels.video ? "video" : ""].filter(Boolean),
      intent: c.intent, block: c.blockMin,
    }));
    const fromRoster = (roster.data?.services || []).map((svc) => {
      const rich = byName.get(svc.label.toLowerCase());
      return { id: svc.id, label: svc.label, dur: rich ? sDuration(rich) : 0, methods: rich ? sMethods(rich) : [], intent: "", block: 0 };
    });
    const list = (fromCatalogue.length ? fromCatalogue : fromRoster).sort((a, b) => a.label.localeCompare(b.label));
    const own = new Set(scope.data?.myServices ?? []);
    const mineCount = list.filter((s) => (own.size ? own.has(s.id) : mySkills.some((k) => skillMatches(k, s)))).length;
    return { items: list, total: list.length, offered: mineCount };
  }, [tick, roster.data, mySkills, scope.data]);

  type Row = { id: string; label: string; dur: number; methods: string[]; intent?: string; block?: number };
  /* Capability is catalogue data (services.json advisors[].services), served on the
     self-scoped read. The roster's skill STRINGS are the fallback for a tenant with no
     catalogue — they use the pre-family-tree vocabulary and match nothing here. */
  const myServiceIds = useMemo(() => new Set(scope.data?.myServices ?? []), [scope.data]);
  const isMine = (s: Row) => (myServiceIds.size ? myServiceIds.has(s.id) : mySkills.some((k) => skillMatches(k, s)));
  const effective = (s: Row) => picked[s.id] ?? isMine(s);
  const pending = useMemo(() => {
    const add: string[] = [], remove: string[] = [];
    for (const s of items) {
      const was = myServiceIds.size ? myServiceIds.has(s.id) : mySkills.some((k) => skillMatches(k, s));
      const now = picked[s.id] ?? was;
      if (now && !was) add.push(s.label);
      if (!now && was) remove.push(s.label);
    }
    return { add, remove };
  }, [items, picked, mySkills, myServiceIds]);
  const dirty = pending.add.length + pending.remove.length > 0;
  /** Roster skills that name nothing in the branch service list — surfaced
   *  rather than silently dropped, so "0 of 3" can't read as "offers nothing". */
  const unmatched = useMemo(
    () => (myServiceIds.size ? [] : (myRow?.skills || []).filter((k) => !items.some((s) => skillMatches(k, s)))),
    [myRow, items, myServiceIds],
  );

  const card: React.CSSProperties = { border: `1px solid ${T.border}`, borderRadius: 12, background: T.white, overflow: "hidden" };
  const loading = roster.loading && !roster.data;

  return (
    <Screen rail="wf-myskills">
      <DataSourceStrip screen="wfmyskills" reqId="REQ-124" ops={["getAdvisorRoster", "listServices", "getMyServiceScope"]} />
      <div style={{ padding: "18px 24px 28px", fontFamily: T.font, display: "flex", flexDirection: "column", gap: 14, maxWidth: 900 }}>
        <div style={{ display: "flex", alignItems: "flex-start", gap: 12, flexWrap: "wrap" }}>
          <div style={{ display: "flex", flexDirection: "column", gap: 4, minWidth: 0 }}>
            <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: T.n1 }}>{t("workforce.mySkills.title", "My Skills & Services")}</h1>
            <span style={{ fontSize: 13, color: T.soft }}>
              {myBranch
                ? t("workforce.mySkills.subtitleBranch", "The services you can deliver at") + ` ${myBranch}`
                : t("workforce.mySkills.subtitle", "The services you can deliver")}
              {myRole ? ` · ${myRole}` : ""}
            </span>
          </div>
          <div style={{ marginLeft: "auto", display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 2 }}>
            <span style={{ fontSize: 17, fontWeight: 700, color: T.n1, fontFamily: T.fontMono }}>{offered} / {total}</span>
            <span style={{ fontSize: 11, color: T.soft, textTransform: "uppercase", letterSpacing: 0.05, fontWeight: 700 }}>{t("workforce.mySkills.servicesOffered", "services I offer")}</span>
          </div>
        </div>

        {scope.data && scope.data.myLobs.length ? (
          <div data-testid="myskills-lob" style={{ ...card, padding: "14px 16px", display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
            <span style={{ fontSize: 11, color: T.soft, textTransform: "uppercase", letterSpacing: 0.05, fontWeight: 700 }}>{t("workforce.mySkills.myLineOfBusiness", "My line of business")}</span>
            <span style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
              {scope.data.myLobs.map((l) => (
                <span key={l.id} style={{ fontSize: 12.5, fontWeight: 700, color: T.p1, background: T.p4, border: `1px solid ${alpha(T.p1, 30)}`, borderRadius: 999, padding: "3px 11px" }}>{l.name}</span>
              ))}
            </span>
            <span style={{ fontSize: 11.5, color: T.soft, marginLeft: "auto" }}>
              {scope.data.assignedBy === "operations"
                ? t("workforce.mySkills.setByOps", "Set by Operations — you can't change this")
                : t("workforce.mySkills.fromYourRole", "From your role — you can't change this")}
            </span>
          </div>
        ) : null}
        {dirty ? (
          <div data-testid="myskills-pending" style={{ ...card, borderColor: alpha(T.p1, 45), background: T.p4, padding: "13px 16px", display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap" }}>
            <span style={{ fontSize: 13, color: T.n1, minWidth: 0 }}>
              <b>{pending.add.length ? `${pending.add.length} ${t("workforce.mySkills.toAdd", "to add")}` : ""}{pending.add.length && pending.remove.length ? " · " : ""}{pending.remove.length ? `${pending.remove.length} ${t("workforce.mySkills.toRemove", "to remove")}` : ""}</b>
              {" — "}{t("workforce.mySkills.notSavedYet", "not saved yet")}
            </span>
            <span style={{ display: "flex", gap: 8, marginLeft: "auto" }}>
              <Btn size="sm" variant="secondary" onClick={() => setPicked({})}>{t("workforce.mySkills.reset", "Reset")}</Btn>
              <Btn size="sm" disabled title={t("workforce.mySkills.needsSetMyServices", "Needs setMyServices — no op exists yet (REQ-124)")}>{t("workforce.mySkills.save", "Save changes")}</Btn>
            </span>
          </div>
        ) : null}

        {roster.error ? (
          <div style={{ ...card, borderColor: alpha(T.red1, 35), background: alpha(T.red1, 6), padding: "14px 16px", display: "flex", alignItems: "center", gap: 12 }}>
            <span style={{ fontSize: 13, color: T.n1 }}>{t("workforce.mySkills.loadFailed", "Couldn't load your services.")} {roster.error}</span>
            <Btn size="sm" variant="secondary" style={{ marginLeft: "auto" }} onClick={() => roster.reload()}>{t("workforce.mySkills.retry", "Retry")}</Btn>
          </div>
        ) : loading ? (
          <div style={{ ...card, padding: 40, textAlign: "center", color: T.soft, fontSize: 13 }}>{t("workforce.mySkills.loading", "Loading your services…")}</div>
        ) : items.length === 0 ? (
          <div style={{ ...card, padding: "28px 20px", textAlign: "center", display: "flex", flexDirection: "column", gap: 6 }}>
            <span style={{ fontSize: 14, fontWeight: 700, color: T.n1 }}>{t("workforce.mySkills.noneTitle", "No services to show yet")}</span>
            <span style={{ fontSize: 12.5, color: T.soft, lineHeight: 1.5 }}>
              {t("workforce.mySkills.noneBody", "Your branch's service list came back empty. Riley maintains it in Services.")}
            </span>
          </div>
        ) : (
          <div style={card} data-testid="myskills-group">
            <div style={{ padding: "11px 16px", borderBottom: `1px solid ${T.n6}`, background: alpha(T.n5, 8), display: "flex", alignItems: "center", gap: 10 }}>
              <span style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.06, color: T.soft }}>
                {myBranch ? `${t("workforce.mySkills.servicesAt", "Services at")} ${myBranch}` : t("workforce.mySkills.services", "Services")}
              </span>
              <span style={{ marginLeft: "auto", fontSize: 11.5, color: T.soft, fontFamily: T.fontMono }}>{items.filter((s) => effective(s)).length} / {items.length}</span>
            </div>
            {items.map((s, i) => {
              const on = effective(s);
              const was = isMine(s);
              const removing = was && !on;
              const adding = !was && on;
              return (
                <div key={s.id} data-testid="myskills-row" data-id={s.id}
                  style={{ padding: "12px 16px", borderTop: i === 0 ? "none" : `1px solid ${T.n6}`, display: "flex", alignItems: "center", gap: 14, background: on ? alpha(T.p1, 3) : T.white }}>
                  <div style={{ display: "flex", flexDirection: "column", gap: 4, minWidth: 0, flex: 1 }}>
                    <span style={{ fontSize: 13.5, fontWeight: 700, color: T.n1 }}>{s.label}</span>
                    {s.dur || s.methods.length ? (
                      <span style={{ display: "flex", alignItems: "center", gap: 7, flexWrap: "wrap" }}>
                        {s.dur ? <span style={{ fontSize: 11.5, color: T.soft, fontFamily: T.fontMono }}>{s.dur}m</span> : null}
                        {s.methods.slice(0, 3).map((m) => <MethodChip key={m} id={m} />)}
                      </span>
                    ) : null}
                    {removing ? (
                      <span style={{ fontSize: 11.5, color: T.orange1, lineHeight: 1.4 }}>
                        {t("workforce.mySkills.managerToldOnRemove", "Removing this tells your branch manager, and you stop being bookable for it.")}
                      </span>
                    ) : adding ? (
                      <span style={{ fontSize: 11.5, color: T.p1, lineHeight: 1.4 }}>
                        {t("workforce.mySkills.bookableOnAdd", "You become bookable for this straight away.")}
                      </span>
                    ) : null}
                  </div>
                  <Switch on={on} label={s.label} onChange={(v) => setPicked((p) => ({ ...p, [s.id]: v }))} />
                </div>
              );
            })}
          </div>
        )}

        <div style={{ ...card, padding: "13px 16px", display: "flex", flexDirection: "column", gap: 6 }}>
          <span style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.05, color: T.soft }}>{t("workforce.mySkills.whatsReal", "What's real here")}</span>
          {unmatched.length ? (
            <span style={{ fontSize: 12, color: T.orange1, lineHeight: 1.55 }}>
              {t("workforce.mySkills.unmatchedSkills", "Your roster records these skills, which don't map onto a service in this list:")} <b>{unmatched.join(", ")}</b>. {t("workforce.mySkills.unmatchedWhy", "Skills and services are two different vocabularies today — nothing here assumes one means the other.")}
            </span>
          ) : null}
          <span style={{ fontSize: 12, color: T.n2, lineHeight: 1.55 }}>
            {t("workforce.mySkills.honesty", "The list is your branch's services, read from the advisor roster along with the ones you already offer. Duration and delivery method appear when your role may read the service catalogue. Ticking can't be saved yet — a staff-scoped op to set your own services doesn't exist, and this screen won't write to the service catalogue to fake it. Filed as REQ-124.")}
          </span>
        </div>
      </div>
    </Screen>
  );
}
